import {
  Box,
  IconButton,
  Typography,
  Stack,
  Chip,
  Tooltip,
} from "@mui/material";
import { useState } from "react";

import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import dayjs from "../../../../utils/dayjs";

const settingsToShow = [
  "planning_level",
  "method",
  "forecast_horizon",
  "lookback_period_length",
  "is_locked",
];

export const ShowSettingsComponent = ({
  isLoading,
  currentEngineSettings,
  settingsName,
  settingsVersion,
  isComparing,
  comparedEngineSettings,
  comparedSettingsName,
  comparedSettingsVersion,
}: {
  isLoading: boolean;
  currentEngineSettings: any;
  settingsName: string | null;
  settingsVersion: string | null;
  isComparing: boolean;
  comparedEngineSettings: any;
  comparedSettingsName: string | null;
  comparedSettingsVersion: string | null;
}) => {
  const [showSettings, setShowSettings] = useState(false);

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Tooltip title="See run settings applied to this run">
        <IconButton
          disabled={isLoading}
          onClick={() => {
            setShowSettings(!showSettings);
          }}
          sx={{ mt: 0.5 }}
        >
          <InfoOutlineIcon />
        </IconButton>
      </Tooltip>

      <Box
        className="hover-content"
        sx={{
          display: showSettings ? "block" : "none",
          position: "absolute",
          top: "100%",
          right: "-200%",
          backgroundColor: "white",
          zIndex: 100000,
          whiteSpace: "nowrap",
          overflowX: "auto",
          maxWidth: "calc(100vw - 16px)",
        }}
      >
        <Box
          sx={{
            p: 4,
            minWidth: 400,
            zIndex: 100000,
            backgroundColor: "action.hover",
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            {isComparing ? "Current" : "Applied"} Engine
            Settings&nbsp;&nbsp;&nbsp;
            {settingsName && (
              <Chip
                variant="outlined"
                sx={{
                  backgroundColor: "white",
                }}
                label={
                  settingsName +
                  " " +
                  dayjs.utc(settingsVersion).local().format("MM/DD/YYYY HH:mm")
                }
              />
            )}
          </Typography>

          {currentEngineSettings ? (
            <Stack spacing={1}>
              {Object.entries(currentEngineSettings).map(([key, value]) => {
                if (!settingsToShow.includes(key)) return null;

                let text = "";

                if (Array.isArray(value)) {
                  text = value.join(", ");
                } else if (typeof value === "object") {
                  text = JSON.stringify(value);
                } else {
                  text = String(value);
                }

                if (
                  key === "lookback_period_length" &&
                  currentEngineSettings["lookback_period_string"]
                ) {
                  text += ` ${currentEngineSettings["lookback_period_string"]}`;
                }

                return (
                  <Stack
                    direction="row"
                    spacing={1}
                    key={key}
                    sx={{
                      whiteSpace: "nowrap",
                      overflowX: "auto",
                    }}
                  >
                    <Typography fontWeight="medium">
                      {key.replaceAll("_", " ")}:
                    </Typography>
                    <Typography>{text}</Typography>
                  </Stack>
                );
              })}
            </Stack>
          ) : (
            <Typography>No engine settings found</Typography>
          )}
        </Box>

        {isComparing && (
          <Box
            sx={{
              p: 4,
              minWidth: 400,
              zIndex: 100000,
              backgroundColor: "action.hover",
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Compared Engine Settings&nbsp;&nbsp;&nbsp;
              {comparedSettingsName && (
                <Chip
                  variant="outlined"
                  sx={{
                    backgroundColor: "white",
                  }}
                  label={
                    comparedSettingsName +
                    " " +
                    dayjs
                      .utc(comparedSettingsVersion)
                      .local()
                      .format("MM/DD/YYYY HH:mm")
                  }
                />
              )}
            </Typography>

            {comparedEngineSettings ? (
              <Stack spacing={1}>
                {Object.entries(comparedEngineSettings).map(([key, value]) => {
                  if (!settingsToShow.includes(key)) return null;

                  let text = "";

                  if (Array.isArray(value)) {
                    text = value.join(", ");
                  } else if (typeof value === "object") {
                    text = JSON.stringify(value);
                  } else {
                    text = String(value);
                  }

                  if (
                    key === "lookback_period_length" &&
                    comparedEngineSettings["lookback_period_string"]
                  ) {
                    text += ` ${comparedEngineSettings["lookback_period_string"]}`;
                  }

                  return (
                    <Stack
                      direction="row"
                      spacing={1}
                      key={key}
                      sx={{
                        whiteSpace: "nowrap",
                        overflowX: "auto",
                      }}
                    >
                      <Typography fontWeight="medium">
                        {key.replaceAll("_", " ")}:
                      </Typography>
                      <Typography>{text}</Typography>
                    </Stack>
                  );
                })}
              </Stack>
            ) : (
              <Typography>No engine settings found</Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
